import { SortDirection } from '@mxa-argos/core/graphql/graphql';
import { graphqlClient } from '@mxa-argos/core/graphql/graphql-client';
import {
  BaseRecord,
  DataProvider,
  GetListParams,
  GetListResponse,
  GetOneParams,
  GetOneResponse,
} from '@refinedev/core';
import createDataProvider from '@refinedev/graphql';

interface PaginatedOutput {
  values: [];
  total: number;
}

interface FilterCriteriaInput {
  searchString?: string;
  orderBy?: { field: string; sortDirection?: SortDirection };
  pagination?: { take?: number; skip?: number };
  [key: string]: unknown;
}

interface MetaVariables {
  criteria?: FilterCriteriaInput;
  [key: string]: unknown;
}

function isPaginatedOutput(output: unknown): boolean {
  if (typeof output !== 'object' || output === null) {
    return false;
  }

  const object = output as Record<string, unknown>;
  return Array.isArray(object.values) && typeof object.total === 'number';
}

const baseProvider = createDataProvider(graphqlClient);

export const dataProvider: DataProvider = {
  ...baseProvider,
  getList: async <TData extends BaseRecord = BaseRecord>(
    params: GetListParams,
  ): Promise<GetListResponse<TData>> => {
    const criteria: FilterCriteriaInput = {};
    const gqlQuery = params.meta?.gqlQuery;
    const operationName = params.meta?.operation;

    const { currentPage = 1, pageSize = 10, mode = 'server' } = params.pagination ?? {};

    if (mode === 'server') {
      Object.assign(criteria, {
        pagination: {
          skip: (currentPage - 1) * pageSize,
          take: pageSize,
        },
      });
    }

    const [sorter] = params.sorters ?? [];

    if (sorter) {
      criteria.orderBy = {
        field: sorter.field,
        sortDirection: sorter.order === 'asc' ? SortDirection.ASC : SortDirection.DESC,
      };
    }

    if (!gqlQuery || !operationName) {
      console.error(new Error('Cannot request if gql query is not set'));
      return { data: [], total: 0 };
    }

    try {
      const metaVariables = params.meta?.variables as MetaVariables | undefined;
      const baseCriteria = metaVariables?.criteria ?? {};
      const finalCriteria = { ...baseCriteria, ...criteria };
      const variables = metaVariables
        ? { ...metaVariables, criteria: finalCriteria }
        : { criteria: finalCriteria };

      const response = await graphqlClient.query(gqlQuery, variables);

      if (response.error) {
        throw Error(response.error.message);
      }
      const data = response.data[operationName];

      if (isPaginatedOutput(data)) {
        return {
          data: (data as PaginatedOutput).values,
          total: (data as PaginatedOutput).total,
        };
      }

      if (Array.isArray(data)) {
        return {
          data: data as [],
          total: data.length,
        };
      }

      throw Error('Received a non-list value');
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  getOne: async <TData extends BaseRecord = BaseRecord>(
    params: GetOneParams,
  ): Promise<GetOneResponse<TData>> => {
    const gqlQuery = params.meta?.gqlQuery;
    const operationName = params.meta?.operation;
    const variables = params.meta?.variables;

    if (!gqlQuery || !operationName) {
      console.error(new Error('Cannot request if gql query is not set'));
      throw new Error('Cannot request if gql query is not set');
    }

    try {
      const response = await graphqlClient.query(gqlQuery, variables ?? {});

      if (response.error) {
        throw Error(response.error.message);
      }

      const data = response.data[operationName];

      return { data: data as TData };
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
};
